<script setup lang="ts">
import type { NuxtError } from '#app'
import { computed } from 'vue'
import { Button } from '@/components/ui/button'

const props = defineProps<{ error: NuxtError }>()
const router = useRouter()

const is404 = computed(() => props.error.statusCode === 404)

function handleError() {
  if (is404.value) {
    router.back()
  }
  else {
    clearError({ redirect: '/dashboard' })
  }
}
</script>

<template>
  <main class="bg-[#fafafa]">
    <div class="max-w-[489px] mx-auto flex flex-col min-h-screen pb-20">
      <nuxt-link to="/" class="flex-none flex justify-center mt-12">
        <img src="/favicon.svg" alt="Rintisan Logo" width="48" height="48">
      </nuxt-link>
      <div class="flex-1 flex flex-col items-center justify-center">
        <div>
          <svg width="51" height="52" viewBox="0 0 51 52" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_i_6146_155829)">
              <path d="M33.3333 4.16666H16.6667C9.375 4.16666 6.25 8.33333 6.25 14.5833V35.4167C6.25 41.6667 9.375 45.8333 16.6667 45.8333H33.3333C40.625 45.8333 43.75 41.6667 43.75 35.4167V14.5833C43.75 8.33333 40.625 4.16666 33.3333 4.16666ZM21.9375 34.3125C22.5417 34.9167 22.5417 35.9167 21.9375 36.5208C21.625 36.8333 21.2292 36.9792 20.8333 36.9792C20.4375 36.9792 20.0417 36.8333 19.7292 36.5208L15.5625 32.3542C14.9583 31.75 14.9583 30.75 15.5625 30.1458L19.7292 25.9792C20.3333 25.375 21.3333 25.375 21.9375 25.9792C22.5417 26.5833 22.5417 27.5833 21.9375 28.1875L18.875 31.25L21.9375 34.3125ZM34.4375 32.3542L30.2708 36.5208C29.9583 36.8333 29.5625 36.9792 29.1667 36.9792C28.7708 36.9792 28.375 36.8333 28.0625 36.5208C27.4583 35.9167 27.4583 34.9167 28.0625 34.3125L31.125 31.25L28.0625 28.1875C27.4583 27.5833 27.4583 26.5833 28.0625 25.9792C28.6667 25.375 29.6667 25.375 30.2708 25.9792L34.4375 30.1458C35.0417 30.75 35.0417 31.75 34.4375 32.3542ZM38.5417 19.2708H34.375C31.2083 19.2708 28.6458 16.7083 28.6458 13.5417V9.375C28.6458 8.52083 29.3542 7.8125 30.2083 7.8125C31.0625 7.8125 31.7708 8.52083 31.7708 9.375V13.5417C31.7708 14.9792 32.9375 16.1458 34.375 16.1458H38.5417C39.3958 16.1458 40.1042 16.8542 40.1042 17.7083C40.1042 18.5625 39.3958 19.2708 38.5417 19.2708Z" fill="#FECBD4" />
              <rect x="30.5" y="31.5" width="20" height="20" rx="10" fill="white" />
              <g clip-path="url(#clip0_6146_155829)">
                <path d="M44.2913 44.5273L46.6049 46.8403L45.8405 47.6047L43.5275 45.2911C42.6669 45.981 41.5964 46.3563 40.4934 46.3547C37.8098 46.3547 35.6318 44.1767 35.6318 41.4932C35.6318 38.8096 37.8098 36.6316 40.4934 36.6316C43.1769 36.6316 45.3549 38.8096 45.3549 41.4932C45.3565 42.5962 44.9812 43.6667 44.2913 44.5273ZM43.2077 44.1265C43.8933 43.4215 44.2761 42.4765 44.2746 41.4932C44.2746 39.4038 42.5822 37.712 40.4934 37.712C38.404 37.712 36.7122 39.4038 36.7122 41.4932C36.7122 43.582 38.404 45.2744 40.4934 45.2744C41.4767 45.2759 42.4217 44.8931 43.1267 44.2075L43.2077 44.1265Z" fill="#F01B4A" />
              </g>
            </g>
            <defs>
              <filter id="filter0_i_6146_155829" x="0" y="0" width="50.5" height="53.5" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset dy="2" />
                <feGaussianBlur stdDeviation="1" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                <feBlend mode="normal" in2="shape" result="effect1_innerShadow_6146_155829" />
              </filter>
              <clipPath id="clip0_6146_155829">
                <rect width="12.9641" height="12.9641" fill="white" transform="translate(34.5518 35.5513)" />
              </clipPath>
            </defs>
          </svg>
        </div>
        <div class="text-center mt-4">
          <h3 class="font-semibold">
            Snaps!
          </h3>
          <p class="text-muted-foreground text-sm">
            Web yang kamu tuju <br> tidak dapat ditemukan!
          </p>
        </div>
        <Button variant="gradient" size="lg" class="mt-8" @click="handleError">
          Kembali
        </Button>
      </div>
      <div class="flex-none flex justify-center text-center">
        <p>
          {{ error.statusCode }}
        </p>
        <p v-if="!is404" class="mt-2">
          {{ error.message }}
        </p>
      </div>
    </div>
  </main>
</template>
